import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
	Badge,
	Box,
	Button,
	Group,
	NumberInput,
	Paper,
	Select,
	Table,
	Text,
	Textarea,
	TextInput,
	Title,
} from '@mantine/core'
import { getOrgDetails } from '../services/actions/orgActions'
import {
	assignOrgPlan,
	getBillingPlans,
	getOrgBillingSettings,
	getOrgEffectiveBillingConfig,
	getOrgInvoicePreview,
	getOrgInvoiceSnapshots,
	getOrgPlanLimits,
	getOrgUsagePreview,
	reconcileOrgStripeBilling,
	runOrgMonthEndBilling,
	updateOrgBillingOverrides,
} from '../services/actions/controlActions'

const monthOptions = [
	{ value: '1', label: 'January' },
	{ value: '2', label: 'February' },
	{ value: '3', label: 'March' },
	{ value: '4', label: 'April' },
	{ value: '5', label: 'May' },
	{ value: '6', label: 'June' },
	{ value: '7', label: 'July' },
	{ value: '8', label: 'August' },
	{ value: '9', label: 'September' },
	{ value: '10', label: 'October' },
	{ value: '11', label: 'November' },
	{ value: '12', label: 'December' },
]

const emptyOverrides = {
	monthlyBasePrice: '',
	maxAdmins: '',
	maxUsers: '',
	includedCheckInsPerMonth: '',
	smsPrice: '',
	mmsPrice: '',
	emailPrice: '',
	checkInPricingTiers: '',
	notes: '',
}

const toStringValue = (value) => {
	if (value === null || value === undefined) return ''
	return String(value)
}

const parseNullableInt = (value, fieldName, { min = 0 } = {}) => {
	const trimmed = String(value || '').trim()
	if (!trimmed) return null
	const parsed = Number(trimmed)
	if (!Number.isInteger(parsed)) {
		throw new Error(`${fieldName} must be an integer.`)
	}
	if (parsed < min) {
		throw new Error(`${fieldName} must be ${min} or greater.`)
	}
	return parsed
}

const parseNullableMoney = (value, fieldName) => {
	const trimmed = String(value || '').trim()
	if (!trimmed) return null
	if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
		throw new Error(`${fieldName} must be a valid decimal number.`)
	}
	if (Number(trimmed) < 0) {
		throw new Error(`${fieldName} cannot be negative.`)
	}
	return trimmed
}

const parseOverridePayload = (formState) => {
	const pricingTierText = String(formState.checkInPricingTiers || '').trim()
	let parsedTiers = null
	if (pricingTierText) {
		try {
			parsedTiers = JSON.parse(pricingTierText)
		} catch {
			throw new Error('checkInPricingTiers must be valid JSON.')
		}
	}

	return {
		monthlyBasePrice: parseNullableMoney(
			formState.monthlyBasePrice,
			'monthlyBasePrice',
		),
		maxAdmins: parseNullableInt(formState.maxAdmins, 'maxAdmins', { min: 1 }),
		maxUsers: parseNullableInt(formState.maxUsers, 'maxUsers', { min: 1 }),
		includedCheckInsPerMonth: parseNullableInt(
			formState.includedCheckInsPerMonth,
			'includedCheckInsPerMonth',
			{ min: 0 },
		),
		smsPrice: parseNullableMoney(formState.smsPrice, 'smsPrice'),
		mmsPrice: parseNullableMoney(formState.mmsPrice, 'mmsPrice'),
		emailPrice: parseNullableMoney(formState.emailPrice, 'emailPrice'),
		checkInPricingTiers: parsedTiers,
		notes: String(formState.notes || '').trim() || null,
	}
}

const formatMonthEndBillingError = (err) => {
	const details = Array.isArray(err?.details)
		? err.details
		: Array.isArray(err?.summary?.details)
			? err.summary.details
			: []
	const failedDetail =
		details.find(
			(detail) => String(detail?.status || '').toUpperCase() === 'FAILED',
		) || details[0]

	if (!failedDetail) {
		return err?.message || 'Failed to run month-end billing.'
	}

	const orgLabel =
		failedDetail.orgSlug ||
		failedDetail.orgId ||
		failedDetail.org ||
		'unknown org'
	const periodLabel =
		failedDetail.year && failedDetail.month
			? `${failedDetail.year}-${String(failedDetail.month).padStart(2, '0')}`
			: 'unknown period'
	const detailMessage =
		failedDetail.stripeSyncError ||
		failedDetail.error ||
		err?.message ||
		'Unknown billing sync error.'
	const stripeStatus = failedDetail.stripeSyncStatus
		? ` Stripe sync status: ${failedDetail.stripeSyncStatus}.`
		: ''

	return `Month-end billing failed for ${orgLabel} ${periodLabel}: ${detailMessage}${stripeStatus}`
}

const formatMoney = (value) => {
	if (value === null || value === undefined || value === '') return '-'
	return `$${value}`
}

const formatCurrency = (value) => {
	if (value === null || value === undefined || value === '') return '-'
	const numeric = Number(value)
	if (!Number.isFinite(numeric)) return '-'
	return new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(numeric)
}

const toNumericAmount = (value) => {
	const numeric = Number(value || 0)
	return Number.isFinite(numeric) ? numeric : 0
}

const toRoundedCents = (value) => Math.round(toNumericAmount(value) * 100)

const getUsageChargeTotal = (usage) =>
	[
		usage?.checkInCharges,
		usage?.smsCharges,
		usage?.mmsCharges,
		usage?.emailCharges,
	].reduce((totalCents, amount) => totalCents + toRoundedCents(amount), 0) / 100

const getSnapshotUsageChargeTotal = (snapshot) =>
	[
		snapshot?.checkInCharge,
		snapshot?.smsCharge,
		snapshot?.mmsCharge,
		snapshot?.emailCharge,
	].reduce((totalCents, amount) => totalCents + toRoundedCents(amount), 0) / 100

const isBillingPeriodClosed = (year, month, now = new Date()) => {
	const parsedYear = Number(year)
	const parsedMonth = Number(month)
	if (!Number.isInteger(parsedYear) || !Number.isInteger(parsedMonth))
		return false
	const periodEndUtc = new Date(
		Date.UTC(parsedYear, parsedMonth, 1, 0, 0, 0, 0),
	)
	return periodEndUtc.getTime() <= now.getTime()
}

const formatDateTimeUtc = (value) => {
	if (!value) return '-'
	const parsed = new Date(value)
	if (Number.isNaN(parsed.getTime())) return '-'
	return parsed.toISOString()
}

const getCheckInTierPrices = (tiers) => {
	const parsedTiers = Array.isArray(tiers) ? tiers : []
	const firstCappedTier =
		parsedTiers.find((tier) => Number(tier?.upTo) === 10000) ||
		parsedTiers.find((tier) => tier?.upTo !== null && tier?.upTo !== undefined)
	const overflowTier = parsedTiers.find(
		(tier) => tier?.upTo === null || tier?.upTo === undefined,
	)

	return {
		first50To10kPrice: firstCappedTier?.unitPrice ?? null,
		after10kPrice: overflowTier?.unitPrice ?? null,
	}
}

const invoiceStatusColorMap = {
	DRAFT: 'gray',
	FINALIZED: 'teal',
	VOID: 'red',
}

const ControlOrgBillingPage = () => {
	const { orgSlug } = useParams()
	const [org, setOrg] = useState(null)
	const [plans, setPlans] = useState([])
	const [settings, setSettings] = useState(null)
	const [effectiveConfig, setEffectiveConfig] = useState(null)
	const [limits, setLimits] = useState(null)
	const [usagePreview, setUsagePreview] = useState(null)
	const [invoicePreview, setInvoicePreview] = useState(null)
	const [invoiceSnapshots, setInvoiceSnapshots] = useState([])

	const [selectedPlanId, setSelectedPlanId] = useState('')
	const [overrideForm, setOverrideForm] = useState(emptyOverrides)

	const now = useMemo(() => new Date(), [])
	const initialYear = now.getFullYear()
	const initialMonth = now.getMonth() + 1
	const [previewYear, setPreviewYear] = useState(initialYear)
	const [previewMonth, setPreviewMonth] = useState(String(initialMonth))

	const [loading, setLoading] = useState(true)
	const [savingPlan, setSavingPlan] = useState(false)
	const [savingOverrides, setSavingOverrides] = useState(false)
	const [refreshingPreviews, setRefreshingPreviews] = useState(false)
	const [refreshingSnapshots, setRefreshingSnapshots] = useState(false)
	const [runningMonthEndBilling, setRunningMonthEndBilling] = useState(false)
	const [reconcilingStripe, setReconcilingStripe] = useState(false)
	const [error, setError] = useState('')
	const [notice, setNotice] = useState('')
	const previewUsageChargeTotal = getUsageChargeTotal(usagePreview?.usage)
	const previewNextCycleBase = toNumericAmount(
		effectiveConfig?.baseMonthlyPrice ||
			invoicePreview?.pricing?.baseMonthlyPrice,
	)
	const previewRenewalTotal = previewUsageChargeTotal + previewNextCycleBase
	const selectedPreviewMonth = Number(previewMonth)
	const selectedSnapshot = invoiceSnapshots.find(
		(snapshot) =>
			Number(snapshot.billingYear) === Number(previewYear) &&
			Number(snapshot.billingMonth) === selectedPreviewMonth,
	)
	const selectedSnapshotSyncStatus = String(
		selectedSnapshot?.stripeSyncStatus || '',
	).toUpperCase()
	const billingPeriodClosed = isBillingPeriodClosed(
		previewYear,
		selectedPreviewMonth,
		now,
	)
	const billingAlreadyFinalizedOrInvoiced =
		Boolean(selectedSnapshot?.stripeInvoiceId) ||
		selectedSnapshotSyncStatus === 'SYNCED' ||
		selectedSnapshotSyncStatus === 'SKIPPED'
	const monthEndBillingDisabled =
		!billingPeriodClosed || billingAlreadyFinalizedOrInvoiced
	const monthEndBillingDisabledReason = !billingPeriodClosed
		? 'Month-end billing is only available for closed UTC calendar months.'
		: billingAlreadyFinalizedOrInvoiced
			? 'Month-end billing has already been synced, skipped, or linked to a Stripe invoice for this month.'
			: ''

	const handleOverrideInputChange = useCallback(
		(fieldName) => (event) => {
			const nextValue = event.currentTarget?.value ?? ''
			setOverrideForm((prev) => ({
				...prev,
				[fieldName]: nextValue,
			}))
		},
		[],
	)

	const populateOverrideForm = (override) => {
		if (!override) {
			setOverrideForm(emptyOverrides)
			return
		}

		setOverrideForm({
			monthlyBasePrice: toStringValue(override.monthlyBasePrice),
			maxAdmins: toStringValue(override.maxAdmins),
			maxUsers: toStringValue(override.maxUsers),
			includedCheckInsPerMonth: toStringValue(
				override.includedCheckInsPerMonth,
			),
			smsPrice: toStringValue(override.smsPrice),
			mmsPrice: toStringValue(override.mmsPrice),
			emailPrice: toStringValue(override.emailPrice),
			checkInPricingTiers: override.checkInPricingTiers
				? JSON.stringify(override.checkInPricingTiers, null, 2)
				: '',
			notes: toStringValue(override.notes),
		})
	}

	const loadBillingState = useCallback(async ({ orgId, year, month }) => {
		const [settingsData, effectiveData, limitsData, usageData, invoiceData] =
			await Promise.all([
				getOrgBillingSettings(orgId),
				getOrgEffectiveBillingConfig(orgId),
				getOrgPlanLimits(orgId),
				getOrgUsagePreview(orgId, year, month),
				getOrgInvoicePreview(orgId, year, month),
			])

		setSettings(settingsData)
		setEffectiveConfig(effectiveData)
		setLimits(limitsData)
		setUsagePreview(usageData)
		setInvoicePreview(invoiceData)
		setSelectedPlanId(settingsData?.plan?.id || '')
		populateOverrideForm(settingsData?.override || null)
	}, [])

	const loadInvoiceSnapshots = useCallback(async ({ orgId, limit = 36 }) => {
		const snapshots = await getOrgInvoiceSnapshots(orgId, { limit })
		setInvoiceSnapshots(snapshots)
	}, [])

	useEffect(() => {
		const boot = async () => {
			setLoading(true)
			setError('')
			try {
				const [orgData, plansData] = await Promise.all([
					getOrgDetails(orgSlug),
					getBillingPlans(),
				])
				setOrg(orgData)
				setPlans(plansData)

				await Promise.all([
					loadBillingState({
						orgId: orgData.id,
						year: initialYear,
						month: initialMonth,
					}),
					loadInvoiceSnapshots({
						orgId: orgData.id,
						limit: 36,
					}),
				])
			} catch (err) {
				console.error('Failed to load billing controls:', err)
				setError(err.message || 'Failed to load billing controls.')
			} finally {
				setLoading(false)
			}
		}

		boot()
	}, [
		orgSlug,
		loadBillingState,
		loadInvoiceSnapshots,
		initialMonth,
		initialYear,
	])

	const handleAssignPlan = async () => {
		if (!org?.id || !selectedPlanId) return
		setSavingPlan(true)
		setNotice('')
		setError('')

		try {
			const result = await assignOrgPlan(org.id, selectedPlanId)
			await loadBillingState({
				orgId: org.id,
				year: previewYear,
				month: Number(previewMonth),
			})
			const warning = String(result?.warning || '').trim()
			setNotice(warning || 'Plan assignment saved.')
		} catch (err) {
			console.error('Failed to assign plan:', err)
			setError(err.message || 'Failed to assign plan.')
		} finally {
			setSavingPlan(false)
		}
	}

	const handleSaveOverrides = async () => {
		if (!org?.id) return
		setSavingOverrides(true)
		setNotice('')
		setError('')

		try {
			const payload = parseOverridePayload(overrideForm)
			const result = await updateOrgBillingOverrides(org.id, payload)
			await loadBillingState({
				orgId: org.id,
				year: previewYear,
				month: Number(previewMonth),
			})
			const warning = String(result?.warning || '').trim()
			const message = String(result?.message || '').trim()
			setNotice(warning || message || 'Billing overrides saved.')
		} catch (err) {
			console.error('Failed to save overrides:', err)
			setError(err.message || 'Failed to save billing overrides.')
		} finally {
			setSavingOverrides(false)
		}
	}

	const handleClearOverrides = async () => {
		if (!org?.id) return
		setSavingOverrides(true)
		setNotice('')
		setError('')

		try {
			const result = await updateOrgBillingOverrides(org.id, {
				monthlyBasePrice: null,
				maxAdmins: null,
				maxUsers: null,
				includedCheckInsPerMonth: null,
				checkInPricingTiers: null,
				smsPrice: null,
				mmsPrice: null,
				emailPrice: null,
				notes: null,
			})
			await loadBillingState({
				orgId: org.id,
				year: previewYear,
				month: Number(previewMonth),
			})
			const warning = String(result?.warning || '').trim()
			const message = String(result?.message || '').trim()
			setNotice(warning || message || 'Billing overrides cleared.')
		} catch (err) {
			console.error('Failed to clear overrides:', err)
			setError(err.message || 'Failed to clear billing overrides.')
		} finally {
			setSavingOverrides(false)
		}
	}

	const handleRefreshPreviews = async () => {
		if (!org?.id) return
		setRefreshingPreviews(true)
		setNotice('')
		setError('')
		try {
			await loadBillingState({
				orgId: org.id,
				year: previewYear,
				month: Number(previewMonth),
			})
			setNotice('Usage and invoice previews refreshed.')
		} catch (err) {
			console.error('Failed to refresh previews:', err)
			setError(err.message || 'Failed to refresh previews.')
		} finally {
			setRefreshingPreviews(false)
		}
	}

	const handleRefreshSnapshots = async () => {
		if (!org?.id) return
		setRefreshingSnapshots(true)
		setNotice('')
		setError('')
		try {
			await loadInvoiceSnapshots({
				orgId: org.id,
				limit: 36,
			})
			setNotice('Invoice snapshot history refreshed.')
		} catch (err) {
			console.error('Failed to refresh invoice snapshots:', err)
			setError(err.message || 'Failed to refresh invoice snapshots.')
		} finally {
			setRefreshingSnapshots(false)
		}
	}

	const handleRunMonthEndBilling = async () => {
		if (!org?.id) return
		if (monthEndBillingDisabled) {
			setError(
				monthEndBillingDisabledReason || 'Month-end billing is unavailable.',
			)
			return
		}
		setRunningMonthEndBilling(true)
		setNotice('')
		setError('')
		try {
			const result = await runOrgMonthEndBilling(org.id, {
				year: previewYear,
				month: Number(previewMonth),
			})
			const detail = Array.isArray(result?.summary?.details)
				? result.summary.details[0]
				: null
			const stripeStatus = detail?.stripeSyncStatus || 'n/a'
			setNotice(
				`Month-end billing run complete. Stripe sync status: ${stripeStatus}.`,
			)
			await loadBillingState({
				orgId: org.id,
				year: previewYear,
				month: Number(previewMonth),
			})
			await loadInvoiceSnapshots({
				orgId: org.id,
				limit: 36,
			})
		} catch (err) {
			console.error('Failed to run month-end billing:', err)
			setError(formatMonthEndBillingError(err))
		} finally {
			setRunningMonthEndBilling(false)
		}
	}

	const handleReconcileStripeBilling = async () => {
		if (!org?.id) return
		setReconcilingStripe(true)
		setNotice('')
		setError('')

		try {
			const result = await reconcileOrgStripeBilling(org.id)
			const warningCount = Array.isArray(result?.result?.warnings)
				? result.result.warnings.length
				: 0
			const defaultPm = result?.result?.defaultPaymentMethodId
				? `Default PM: ${result.result.defaultPaymentMethodId}.`
				: 'No default PM in Stripe customer.'
			const warningSuffix =
				warningCount > 0 ? ` ${warningCount} warning(s) returned.` : ''
			setNotice(
				`${result?.message || 'Stripe billing reconciliation completed.'} ${defaultPm}${warningSuffix}`,
			)
			await loadBillingState({
				orgId: org.id,
				year: previewYear,
				month: Number(previewMonth),
			})
			await loadInvoiceSnapshots({
				orgId: org.id,
				limit: 36,
			})
		} catch (err) {
			console.error('Failed to reconcile Stripe billing state:', err)
			setError(err.message || 'Failed to reconcile Stripe billing state.')
		} finally {
			setReconcilingStripe(false)
		}
	}

	if (loading) {
		return (
			<Box px='md' py='md'>
				Loading billing controls...
			</Box>
		)
	}

	return (
		<Box px='md' py='md'>
			<Title order={2} mb='xs'>
				Organization Billing
			</Title>
			<Text c='dimmed' mb='md'>
				{org?.name || orgSlug}
			</Text>

			{error && (
				<Text c='red' mb='sm'>
					{error}
				</Text>
			)}
			{notice && (
				<Text c='teal' mb='sm'>
					{notice}
				</Text>
			)}

			<Paper withBorder p='md' mb='md'>
				<Title order={4} mb='sm'>
					Plan Assignment
				</Title>
				<Group align='flex-end'>
					<Select
						label='Plan'
						placeholder='Select plan'
						data={plans.map((plan) => ({
							value: plan.id,
							label: `${plan.name} (${plan.code})`,
						}))}
						value={selectedPlanId}
						onChange={(value) => setSelectedPlanId(value || '')}
						w={320}
					/>
					<Button onClick={handleAssignPlan} loading={savingPlan}>
						Save Plan
					</Button>
				</Group>

				<Table withColumnBorders mt='md'>
					<tbody>
						<tr>
							<td>Current plan</td>
							<td>
								{settings?.plan?.name || '-'} ({settings?.plan?.code || '-'})
							</td>
						</tr>
						<tr>
							<td>Base monthly</td>
							<td>{formatMoney(effectiveConfig?.baseMonthlyPrice)}</td>
						</tr>
						<tr>
							<td>Status</td>
							<td>
								<Badge>
									{effectiveConfig?.billingStatus ||
										settings?.org?.billingStatus ||
										'ACTIVE'}
								</Badge>
							</td>
						</tr>
						<tr>
							<td>Activated At (UTC)</td>
							<td>
								{formatDateTimeUtc(
									effectiveConfig?.billingLifecycle?.activatedAt,
								)}
							</td>
						</tr>
						<tr>
							<td>Past Due At (UTC)</td>
							<td>
								{formatDateTimeUtc(
									effectiveConfig?.billingLifecycle?.pastDueAt,
								)}
							</td>
						</tr>
						<tr>
							<td>Grace Ends At (UTC)</td>
							<td>
								{formatDateTimeUtc(
									effectiveConfig?.billingLifecycle?.graceEndsAt,
								)}
							</td>
						</tr>
						<tr>
							<td>Suspended At (UTC)</td>
							<td>
								{formatDateTimeUtc(
									effectiveConfig?.billingLifecycle?.suspendedAt,
								)}
							</td>
						</tr>
						<tr>
							<td>51-10,000 check-in price</td>
							<td>
								{formatMoney(
									getCheckInTierPrices(effectiveConfig?.checkInPricingTiers)
										.first50To10kPrice,
								)}
							</td>
						</tr>
						<tr>
							<td>10,001+ check-in price</td>
							<td>
								{formatMoney(
									getCheckInTierPrices(effectiveConfig?.checkInPricingTiers)
										.after10kPrice,
								)}
							</td>
						</tr>
						<tr>
							<td>SMS price</td>
							<td>{formatMoney(effectiveConfig?.smsPrice)}</td>
						</tr>
						<tr>
							<td>MMS price</td>
							<td>{formatMoney(effectiveConfig?.mmsPrice)}</td>
						</tr>
						<tr>
							<td>Email price</td>
							<td>{formatMoney(effectiveConfig?.emailPrice)}</td>
						</tr>
					</tbody>
				</Table>
				<Title order={5} mt='md' mb='xs'>
					Plan Catalog
				</Title>
				<Table withColumnBorders striped>
					<thead>
						<tr>
							<th>Name</th>
							<th>Code</th>
							<th>Base Monthly</th>
							<th>Max Admins</th>
							<th>Max Users</th>
							<th>Included Check-Ins</th>
							<th>51-10,000 Price</th>
							<th>10,001+ Price</th>
							<th>SMS Price</th>
							<th>MMS Price</th>
							<th>Email Price</th>
						</tr>
					</thead>
					<tbody>
						{plans.map((plan) => {
							const tierPricing = getCheckInTierPrices(plan.checkInPricingTiers)
							return (
								<tr key={plan.id}>
									<td>{plan.name}</td>
									<td>{plan.code}</td>
									<td>{formatMoney(plan.monthlyBasePrice)}</td>
									<td>{plan.maxAdmins ?? 'Unlimited'}</td>
									<td>{plan.maxUsers ?? 'Unlimited'}</td>
									<td>{plan.includedCheckInsPerMonth}</td>
									<td>{formatMoney(tierPricing.first50To10kPrice)}</td>
									<td>{formatMoney(tierPricing.after10kPrice)}</td>
									<td>{formatMoney(plan.smsPrice)}</td>
									<td>{formatMoney(plan.mmsPrice)}</td>
									<td>{formatMoney(plan.emailPrice)}</td>
								</tr>
							)
						})}
					</tbody>
				</Table>
			</Paper>

			<Paper withBorder p='md' mb='md'>
				<Title order={4} mb='sm'>
					Stripe Reconciliation
				</Title>
				<Text size='sm' c='dimmed' mb='sm'>
					Manually reconcile this org&apos;s Stripe customer default payment
					method and subscription linkage.
				</Text>
				<Button
					variant='light'
					onClick={handleReconcileStripeBilling}
					loading={reconcilingStripe}
				>
					Reconcile Stripe State
				</Button>
			</Paper>

			<Paper withBorder p='md' mb='md'>
				<Title order={4} mb='sm'>
					Org Overrides
				</Title>
				<Text size='sm' c='dimmed' mb='sm'>
					Leave a field empty to use plan defaults. Use `null` behavior by
					clearing values and saving.
				</Text>
				<Group grow>
					<TextInput
						label='Monthly Base Price'
						placeholder='e.g. 59 or 59.00'
						value={overrideForm.monthlyBasePrice}
						onChange={handleOverrideInputChange('monthlyBasePrice')}
					/>
					<TextInput
						label='Included Check-Ins / Month'
						placeholder='e.g. 50'
						value={overrideForm.includedCheckInsPerMonth}
						onChange={handleOverrideInputChange('includedCheckInsPerMonth')}
					/>
				</Group>

				<Group grow mt='sm'>
					<TextInput
						label='Max Admins'
						placeholder='e.g. 10'
						value={overrideForm.maxAdmins}
						onChange={handleOverrideInputChange('maxAdmins')}
					/>
					<TextInput
						label='Max Users'
						placeholder='e.g. 50'
						value={overrideForm.maxUsers}
						onChange={handleOverrideInputChange('maxUsers')}
					/>
				</Group>

				<Group grow mt='sm'>
					<TextInput
						label='SMS Price'
						placeholder='e.g. 0.04'
						value={overrideForm.smsPrice}
						onChange={handleOverrideInputChange('smsPrice')}
					/>
					<TextInput
						label='MMS Price'
						placeholder='e.g. 0.06'
						value={overrideForm.mmsPrice}
						onChange={handleOverrideInputChange('mmsPrice')}
					/>
					<TextInput
						label='Email Price'
						placeholder='e.g. 0.0025'
						value={overrideForm.emailPrice}
						onChange={handleOverrideInputChange('emailPrice')}
					/>
				</Group>

				<Textarea
					label='Check-In Pricing Tiers (JSON)'
					description='Example: [{"upTo":10000,"unitPrice":"0.50"},{"upTo":null,"unitPrice":"0.40"}]'
					minRows={5}
					mt='sm'
					value={overrideForm.checkInPricingTiers}
					onChange={handleOverrideInputChange('checkInPricingTiers')}
				/>

				<Textarea
					label='Contract Notes'
					minRows={2}
					mt='sm'
					value={overrideForm.notes}
					onChange={handleOverrideInputChange('notes')}
				/>

				<Group mt='md'>
					<Button onClick={handleSaveOverrides} loading={savingOverrides}>
						Save Overrides
					</Button>
					<Button
						variant='outline'
						color='gray'
						onClick={handleClearOverrides}
						loading={savingOverrides}
					>
						Clear Overrides
					</Button>
				</Group>
			</Paper>

			<Paper withBorder p='md' mb='md'>
				<Title order={4} mb='sm'>
					Usage & Invoice Preview
				</Title>

				<Group align='flex-end' mb='sm'>
					<NumberInput
						label='Year'
						value={previewYear}
						onChange={(value) =>
							setPreviewYear(Number(value) || now.getFullYear())
						}
						min={2020}
						max={9999}
						w={120}
					/>
					<Select
						label='Month'
						value={previewMonth}
						onChange={(value) =>
							setPreviewMonth(value || String(now.getMonth() + 1))
						}
						data={monthOptions}
						w={180}
					/>
					<Button onClick={handleRefreshPreviews} loading={refreshingPreviews}>
						Refresh Preview
					</Button>
					<Button
						variant='light'
						onClick={handleRunMonthEndBilling}
						loading={runningMonthEndBilling}
						disabled={monthEndBillingDisabled}
					>
						Run Month-End Billing
					</Button>
				</Group>
				{monthEndBillingDisabledReason && (
					<Text size='sm' c='dimmed' mb='sm'>
						{monthEndBillingDisabledReason}
					</Text>
				)}

				<Table withColumnBorders>
					<tbody>
						<tr>
							<td>Total billable check-ins</td>
							<td>{usagePreview?.usage?.totalBillableCheckIns ?? '-'}</td>
						</tr>
						<tr>
							<td>Included check-ins</td>
							<td>{usagePreview?.usage?.includedCheckIns ?? '-'}</td>
						</tr>
						<tr>
							<td>Paid check-ins</td>
							<td>{usagePreview?.usage?.paidCheckIns ?? '-'}</td>
						</tr>
						<tr>
							<td>Check-in charges</td>
							<td>{formatCurrency(usagePreview?.usage?.checkInCharges)}</td>
						</tr>
						<tr>
							<td>Billable SMS</td>
							<td>{usagePreview?.usage?.totalBillableSms ?? '-'}</td>
						</tr>
						<tr>
							<td>SMS charges</td>
							<td>{formatCurrency(usagePreview?.usage?.smsCharges)}</td>
						</tr>
						<tr>
							<td>Billable MMS</td>
							<td>{usagePreview?.usage?.totalBillableMms ?? '-'}</td>
						</tr>
						<tr>
							<td>MMS charges</td>
							<td>{formatCurrency(usagePreview?.usage?.mmsCharges)}</td>
						</tr>
						<tr>
							<td>Billable emails</td>
							<td>{usagePreview?.usage?.totalBillableEmails ?? '-'}</td>
						</tr>
						<tr>
							<td>Email charges</td>
							<td>{formatCurrency(usagePreview?.usage?.emailCharges)}</td>
						</tr>
						<tr>
							<td>Current cycle usage total</td>
							<td>{formatCurrency(previewUsageChargeTotal)}</td>
						</tr>
						<tr>
							<td>Next cycle base</td>
							<td>{formatCurrency(previewNextCycleBase)}</td>
						</tr>
						<tr>
							<td>Estimated invoice total</td>
							<td>{formatCurrency(previewRenewalTotal)}</td>
						</tr>
					</tbody>
				</Table>
			</Paper>

			<Paper withBorder p='md' mb='md'>
				<Group position='apart' mb='sm'>
					<div>
						<Title order={4}>Invoice Snapshot History</Title>
						<Text size='sm' c='dimmed'>
							Internal immutable records used for finance reconciliation and
							support.
						</Text>
					</div>
					<Button
						variant='light'
						onClick={handleRefreshSnapshots}
						loading={refreshingSnapshots}
					>
						Refresh
					</Button>
				</Group>

				{invoiceSnapshots.length === 0 ? (
					<Text c='dimmed' size='sm'>
						No snapshots yet for this organization.
					</Text>
				) : (
					<Table striped highlightOnHover withColumnBorders>
						<thead>
							<tr>
								<th>Period</th>
								<th>Status</th>
								<th>Plan</th>
								<th>Check-Ins</th>
								<th>SMS</th>
								<th>MMS</th>
								<th>Emails</th>
								<th>Usage Total</th>
								<th>Stripe Sync</th>
								<th>Stripe Invoice</th>
								<th>Created (UTC)</th>
							</tr>
						</thead>
						<tbody>
							{invoiceSnapshots.map((snapshot) => (
								<tr key={snapshot.id}>
									<td>{`${snapshot.billingYear}-${String(snapshot.billingMonth).padStart(2, '0')}`}</td>
									<td>
										<Badge
											color={invoiceStatusColorMap[snapshot.status] || 'gray'}
											variant='light'
										>
											{snapshot.status}
										</Badge>
									</td>
									<td>{snapshot.planName || snapshot.planCode || '-'}</td>
									<td>{snapshot.checkInUsage}</td>
									<td>{snapshot.smsUsage}</td>
									<td>{snapshot.mmsUsage}</td>
									<td>{snapshot.emailUsage}</td>
									<td>
										{formatCurrency(getSnapshotUsageChargeTotal(snapshot))}
									</td>
									<td>
										<Badge
											color={
												snapshot.stripeSyncStatus === 'SYNCED'
													? 'teal'
													: snapshot.stripeSyncStatus === 'FAILED'
														? 'red'
														: snapshot.stripeSyncStatus === 'QUEUED'
															? 'blue'
															: 'gray'
											}
											variant='light'
										>
											{snapshot.stripeSyncStatus || 'PENDING'}
										</Badge>
									</td>
									<td>
										{snapshot.stripeInvoiceNumber ||
											snapshot.stripeInvoiceId ||
											'-'}
									</td>
									<td>{formatDateTimeUtc(snapshot.createdAt)}</td>
								</tr>
							))}
						</tbody>
					</Table>
				)}
			</Paper>

			<Paper withBorder p='md'>
				<Title order={4} mb='sm'>
					Plan Limits Status
				</Title>
				<Table withColumnBorders>
					<tbody>
						<tr>
							<td>Can add user</td>
							<td>{limits?.canAddUser?.allowed ? 'Yes' : 'No'}</td>
						</tr>
						<tr>
							<td>Users</td>
							<td>
								{limits?.canAddUser?.currentUsers ?? '-'} /{' '}
								{limits?.canAddUser?.maxUsers ?? 'Unlimited'}
							</td>
						</tr>
						<tr>
							<td>Can add admin</td>
							<td>{limits?.canAddAdmin?.allowed ? 'Yes' : 'No'}</td>
						</tr>
						<tr>
							<td>Admins</td>
							<td>
								{limits?.canAddAdmin?.currentAdmins ?? '-'} /{' '}
								{limits?.canAddAdmin?.maxAdmins ?? 'Unlimited'}
							</td>
						</tr>
					</tbody>
				</Table>
			</Paper>
		</Box>
	)
}

export default ControlOrgBillingPage
